import { AxiosError } from 'axios';
import { sanitizeUrlPath, trackException } from '../diagnostics/applicationInsights';
import { SimManagementError, ValidationErrors } from '../models/Errors';

export interface ActionResult {
  isSuccess: boolean;
  message: string;
}

/**
 * Generates a Sim Management error from an Axios error returned from a failed AJAX call.
 * @param axiosError The Axios error object.
 */
export function getSimManagementErrorFromAxiosError(axiosError: AxiosError): SimManagementError {
  const { config, message, response } = axiosError;
  const error = new SimManagementError(message, response?.status ?? 0);
  trackException(error, {
    source: 'api.failure',
    method: config?.method?.toUpperCase(),
    path: sanitizeUrlPath(config?.url),
    statusCode: response?.status ?? 0,
    requestId:
      response?.headers?.['x-request-id'] ||
      response?.headers?.['x-correlation-id'] ||
      response?.headers?.['request-id'] ||
      response?.headers?.['traceparent'],
  });

  if (response) {
    if (response.status === 400) {
      // got an HTTP 400, may contain validation errors
      error.validationErrors = response.data as ValidationErrors;
      error.message = `Error: ${error.statusCode} ${formatValidationErrorMessage(error.validationErrors)}`;
    } else if (response.status === 409 && response.data && response.data !== "") {
      // A 409 body is a plain string message; axios 1.x types response.data as unknown.
      error.message = response.data as string;
    }
  }

  return error;
}

function formatValidationErrorMessage(validationErrors?: ValidationErrors): string {
  if (!validationErrors) {
    return '';
  }

  if (typeof validationErrors === 'string') {
    return validationErrors;
  }

  return Object.keys(validationErrors)
    .map((key) => {
      const value = validationErrors[key];
      if (Array.isArray(value)) {
        return `${key}: ${value.join(', ')}`;
      }

      return `${key}: ${String(value)}`;
    })
    .join(' | ');
}
